import DG_CONFIG from "../../config/data-grid";
import logger from "../../helpers/logger";

// Cookie service for managing user preferences
class CookieService {
  /**
   * Set a cookie with optional expiration
   * @param {string} name - Cookie name
   * @param {string} value - Cookie value
   * @param {number} days - Expiration in days (default: 365)
   * @param {string} path - Cookie path (default: '/')
   */
  setCookie(name, value, days = 365, path = "/") {
    try {
      const expires = new Date();
      expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);

      const cookieString = `${name}=${encodeURIComponent(
        value
      )}; expires=${expires.toUTCString()}; path=${path}; SameSite=Strict`;
      document.cookie = cookieString;

      logger.log(`Cookie set: ${name}=${value}`);
    } catch (error) {
      logger.error(`Failed to set cookie "${name}":`, error);
    }
  }

  /**
   * Get a cookie value by name
   * @param {string} name - Cookie name
   * @returns {string|null} Cookie value or null if not found
   */
  getCookie(name) {
    try {
      const nameEQ = name + "=";
      const cookies = document.cookie.split(";");

      for (let cookie of cookies) {
        let c = cookie.trim();
        if (c.indexOf(nameEQ) === 0) {
          const value = decodeURIComponent(c.substring(nameEQ.length));

          if (name.toLowerCase().endsWith("status")) {
            const allowedValues = ["all", "active", "inactive"];
            if (!allowedValues.includes(value)) {
              return null;
            }
          }
          else if (name.toLowerCase().endsWith("pagesize")) {
            const allowedValues = DG_CONFIG.PAGE_SIZE_OPTIONS.map(opt => opt.value);
            const numericValue = parseInt(value, 10);
            if (!allowedValues.includes(numericValue)) {
              return null;
            }
          }
          else if (name.toLowerCase().endsWith("sortmodel")) {
            const parsedValue = JSON.parse(value);
            if (parsedValue === null && typeof parsedValue !== "object") {
              return null;
            }
          }

          logger.log(`Cookie retrieved: ${name}=${value}`);
          return value;
        }
      }

      logger.log(`Cookie not found: ${name}`);
      return null;
    } catch (error) {
      logger.error(`Failed to get cookie "${name}":`, error);
      return null;
    }
  }

  /**
   * Delete a cookie by name
   * @param {string} name - Cookie name
   * @param {string} path - Cookie path (default: '/')
   */
  deleteCookie(name, path = "/") {
    try {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}`;
      logger.log(`Cookie deleted: ${name}`);
    } catch (error) {
      logger.error(`Failed to delete cookie "${name}":`, error);
    }
  }

  /**
   * Set a user preference cookie with JSON value
   * @param {string} key - Preference key
   * @param {any} value - Preference value
   */
  setPreference(key, value) {
    try {
      const val = typeof value === "object" ? JSON.stringify(value) : value;
      this.setCookie(`rackstar_pref_${key}`, val);
    } catch (error) {
      logger.error(`Failed to set preference "${key}":`, error);
    }
  }

  /**
   * Get a user preference from cookies
   * @param {string} key - Preference key
   * @param {any} defaultValue - Default value if preference not found
   * @returns {any} Preference value or default
   */
  getPreference(key, defaultValue = null) {
    try {
      const cookieValue = this.getCookie(`rackstar_pref_${key}`);
      if (!cookieValue) return defaultValue;

      try {
        return JSON.parse(cookieValue);
      } catch {
        // Not JSON, return raw string
        return cookieValue;
      }
    } catch (error) {
      logger.error(`Failed to get preference "${key}":`, error);
      return defaultValue;
    }
  }

  /**
   * Delete a user preference cookie
   * @param {string} key - Preference key
   */
  deletePreference(key) {
    this.deleteCookie(`rackstar_pref_${key}`);
  }

  /**
   * Get boolean preference with proper type conversion
   * @param {string} key - Preference key
   * @param {boolean} defaultValue - Default boolean value
   * @returns {boolean} Boolean preference value
   */
  getBooleanPreference(key, defaultValue = false) {
    try {
      const value = this.getPreference(key, defaultValue);
      // Handle string representations of booleans
      if (typeof value === "string") {
        return value.toLowerCase() === "true";
      }
      return Boolean(value);
    } catch (error) {
      logger.error(`Failed to get boolean preference "${key}":`, error);
      return defaultValue;
    }
  }

  // -------------------------
  // Component-based Preferences
  // -------------------------

  /**
   * Store preference scoped to a specific component
   * @param {string} componentName
   * @param {string} key
   * @param {any} value
   */
  setComponentPreference(componentName, key, value) {
    try {
      const prefKey = `${componentName}_${key}`;
      this.setPreference(prefKey, value);
      logger.log(`Component preference set: ${componentName}.${key} = ${JSON.stringify(value)}`);
    } catch (error) {
      logger.error(`Failed to set component preference for ${componentName}.${key}:`, error);
    }
  }

  /**
   * Retrieve preference scoped to a specific component
   * @param {string} componentName
   * @param {string} key
   * @param {any} defaultValue
   * @returns {any}
   */
  getComponentPreference(componentName, key, defaultValue = null) {
    try {
      const prefKey = `${componentName}_${key}`;
      const prefValue = this.getPreference(prefKey, defaultValue);
      return prefValue == null ? defaultValue : prefValue;
    } catch (error) {
      logger.error(`Failed to get component preference for ${componentName}.${key}:`, error);
      return defaultValue;
    }
  }

  /**
   * Delete a preference scoped to a specific component
   * @param {string} componentName
   * @param {string} key
   */
  deleteComponentPreference(componentName, key) {
    try {
      const prefKey = `${componentName}_${key}`;
      this.deletePreference(prefKey);
      logger.log(`Component preference deleted: ${componentName}.${key}`);
    } catch (error) {
      logger.error(`Failed to delete component preference for ${componentName}.${key}:`, error);
    }
  }
}

// Export a singleton instance
const cookieService = new CookieService();
export default cookieService;

// Named exports for convenience
export const setCookie = cookieService.setCookie.bind(cookieService);
export const getCookie = cookieService.getCookie.bind(cookieService);
export const deleteCookie = cookieService.deleteCookie.bind(cookieService);
export const setPreference = cookieService.setPreference.bind(cookieService);
export const getPreference = cookieService.getPreference.bind(cookieService);
export const deletePreference = cookieService.deletePreference.bind(cookieService);
export const getBooleanPreference = cookieService.getBooleanPreference.bind(cookieService);
export const setComponentPreference = cookieService.setComponentPreference.bind(cookieService);
export const getComponentPreference = cookieService.getComponentPreference.bind(cookieService);
export const deleteComponentPreference = cookieService.deleteComponentPreference.bind(cookieService);

